// src/hooks/insights/useInsightsData.ts
//
// Fires the InsightsDAO calls in parallel for the resolved org code and selected date
// range. Each section gets independent { data, loading, error } state so one failed
// section (e.g. a Cube timeout on the trend query) never blanks the others - there is no
// single "isLoading"/"error" flag for the whole page.

import { useEffect, useRef, useState, type Dispatch, type SetStateAction } from 'react';
import {
  getComplianceTrend,
  getCoreProDeviceCounts,
  getDeviceStatusBreakdown,
  getFeatureCompliance,
  getFleetStatus,
  getProtectedHours,
  getReportingCoverage,
  getUtilization,
  getVersionDistribution,
  getWorkZoneBreaches,
} from '@/repository/InsightsDAO';
import type {
  ComplianceTrendResult,
  CoreProDeviceCountSummary,
  DeviceStatusSlice,
  FeatureComplianceSummary,
  FleetStatus,
  ProtectedHoursSummary,
  UtilizationSummary,
  VersionDistribution,
  WorkZoneBreachQueryResult,
} from '@/models/Insights/InsightsTypes';
import type { InsightsDateRange } from '@/models/Insights/insightsDateRange';
import { useInsightsOrgCode } from '@/hooks/insights/useInsightsOrgCode';

export type InsightsSectionState<T> = {
  data: T | null;
  loading: boolean;
  error: Error | null;
};

export type UseInsightsDataResult = {
  fleetStatus: InsightsSectionState<FleetStatus>;
  deviceStatusBreakdown: InsightsSectionState<DeviceStatusSlice[]>;
  complianceTrend: InsightsSectionState<ComplianceTrendResult>;
  reportingCoverage: InsightsSectionState<number | null>;
  versionDistribution: InsightsSectionState<VersionDistribution>;
  featureCompliance: InsightsSectionState<FeatureComplianceSummary>;
  protectedHours: InsightsSectionState<ProtectedHoursSummary>;
  utilization: InsightsSectionState<UtilizationSummary>;
  /** Backs UtilizationSection's 4th card ("Core vs. Pro Devices") - a separate
   * `DailyDeviceRollup` query from `protectedHours`/`utilization` above (its own
   * `device_key`/`is_core` dimensions-only, distinct-count reduction, not the hours/
   * device-count measures those two read), so it gets its own independent section state
   * rather than being folded into either. Renamed from `coreProCompliance` when this card's
   * metric pivoted from compliance ratios to distinct device counts - see
   * `getCoreProDeviceCounts`'s own header comment (InsightsDAO.ts). */
  coreProDeviceCounts: InsightsSectionState<CoreProDeviceCountSummary>;
  /** Backs `WorkZoneAdaptCard` (M4 task 29) - row-level Work Zone Breach query (PRD D8;
   * design §11.2). `data.truncated` rides along as a normal field of the fetched payload,
   * exactly like every other section here; `WorkZoneAdaptCard` is the one that reads and
   * deliberately discards it (D10 forbids new user-facing copy to disclose truncation - see
   * that component's own header comment for the full reasoning). */
  workZoneBreaches: InsightsSectionState<WorkZoneBreachQueryResult>;
};

const INITIAL_SECTION_STATE: InsightsSectionState<never> = {
  data: null,
  loading: false,
  error: null,
};

function loadingSectionState<T>(previous: InsightsSectionState<T>): InsightsSectionState<T> {
  return { ...previous, loading: true, error: null };
}

/** Runs one DAO call and reports its outcome into the given section's setter, ignoring the
 * result if `isCurrent()` is false by the time it resolves (a newer orgCode/range request
 * has since superseded it - prevents a slow, stale request from clobbering fresh data). */
function runSection<T>(
  setState: Dispatch<SetStateAction<InsightsSectionState<T>>>,
  isCurrent: () => boolean,
  load: () => Promise<T>,
): void {
  setState((previous) => loadingSectionState(previous));
  load().then(
    (data) => {
      if (!isCurrent()) return;
      setState({ data, loading: false, error: null });
    },
    (error: unknown) => {
      if (!isCurrent()) return;
      setState({
        data: null,
        loading: false,
        error: error instanceof Error ? error : new Error(String(error)),
      });
    },
  );
}

/**
 * Orchestrates the Insights page's data sections for the given date range. Guards
 * against firing requests before `orgCode` resolves (Task 2.5/3.5) - the initial render
 * of `useUserUpdaterProvider().orgUser` is null, so an empty orgCode is expected on first
 * paint, not an error. Also guards against firing while `range` is `null`, which the Custom
 * preset produces whenever its picked bounds are incomplete or invalid - firing a coverage
 * query with a missing bound would get back a real SQL `NULL` from `reporting_coverage_ratio`
 * rather than a "not ready yet" signal, so the request must simply not go out. Refetches
 * whenever `orgCode` or `range` changes.
 */
export function useInsightsData(range: InsightsDateRange | null): UseInsightsDataResult {
  const orgCode = useInsightsOrgCode();

  const [fleetStatus, setFleetStatus] =
    useState<InsightsSectionState<FleetStatus>>(INITIAL_SECTION_STATE);
  const [deviceStatusBreakdown, setDeviceStatusBreakdown] =
    useState<InsightsSectionState<DeviceStatusSlice[]>>(INITIAL_SECTION_STATE);
  const [complianceTrend, setComplianceTrend] =
    useState<InsightsSectionState<ComplianceTrendResult>>(INITIAL_SECTION_STATE);
  const [reportingCoverage, setReportingCoverage] =
    useState<InsightsSectionState<number | null>>(INITIAL_SECTION_STATE);
  const [versionDistribution, setVersionDistribution] =
    useState<InsightsSectionState<VersionDistribution>>(INITIAL_SECTION_STATE);
  const [featureCompliance, setFeatureCompliance] =
    useState<InsightsSectionState<FeatureComplianceSummary>>(INITIAL_SECTION_STATE);
  const [protectedHours, setProtectedHours] =
    useState<InsightsSectionState<ProtectedHoursSummary>>(INITIAL_SECTION_STATE);
  const [utilization, setUtilization] =
    useState<InsightsSectionState<UtilizationSummary>>(INITIAL_SECTION_STATE);
  const [coreProDeviceCounts, setCoreProDeviceCounts] =
    useState<InsightsSectionState<CoreProDeviceCountSummary>>(INITIAL_SECTION_STATE);
  const [workZoneBreaches, setWorkZoneBreaches] =
    useState<InsightsSectionState<WorkZoneBreachQueryResult>>(INITIAL_SECTION_STATE);

  // Bumped on every effect run so in-flight requests from a superseded orgCode/range can
  // detect they're stale when they resolve (see runSection's isCurrent check).
  const requestGeneration = useRef(0);

  useEffect(() => {
    if (!orgCode || !range) return;

    requestGeneration.current += 1;
    const generation = requestGeneration.current;
    const isCurrent = () => requestGeneration.current === generation;

    runSection(setFleetStatus, isCurrent, () => getFleetStatus(orgCode));
    runSection(setDeviceStatusBreakdown, isCurrent, () => getDeviceStatusBreakdown(orgCode));
    runSection(setComplianceTrend, isCurrent, () => getComplianceTrend(orgCode, range));
    runSection(setReportingCoverage, isCurrent, () => getReportingCoverage(orgCode, range));
    runSection(setVersionDistribution, isCurrent, () => getVersionDistribution(orgCode));
    runSection(setFeatureCompliance, isCurrent, () => getFeatureCompliance(orgCode, range));
    runSection(setProtectedHours, isCurrent, () => getProtectedHours(orgCode, range));
    runSection(setUtilization, isCurrent, () => getUtilization(orgCode, range));
    // Grouped here with protectedHours/utilization (not up near complianceTrend) because
    // all three read `DailyDeviceRollup` with the same org/range shape - this is the same
    // "reads DailyDeviceRollup" family, just a different dimension pair (see
    // getCoreProDeviceCounts's own header comment for the full reduction reasoning).
    runSection(setCoreProDeviceCounts, isCurrent, () => getCoreProDeviceCounts(orgCode, range));
    runSection(setWorkZoneBreaches, isCurrent, () => getWorkZoneBreaches(orgCode, range));
  }, [orgCode, range]);

  return {
    fleetStatus,
    deviceStatusBreakdown,
    complianceTrend,
    reportingCoverage,
    versionDistribution,
    featureCompliance,
    protectedHours,
    utilization,
    coreProDeviceCounts,
    workZoneBreaches,
  };
}
